import { useState } from "react";
import Sidebar from "@/components/Sidebar";
import { useSubscriptionGuard } from "@/components/SubscriptionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Send, ImageOff, Loader2, ExternalLink, Wand2 } from "lucide-react";
import BatchPublish from "@/pages/BatchPublish";

const emptyForm = {
  name: "",
  category: "",
  description: "",
  usage: "",
  seo_title: "",
  seo_description: "",
  handle: "",
  suggested_price: "",
  price_reasoning: "",
  sku: "",
  stock: 0,
  images: [],
};

// Thumbnail with React-state-based error handling (avoids DOM mutation bugs)
function PublishThumb({ url }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return (
      <div className="w-16 h-16 rounded-md bg-zinc-100 border border-zinc-200 flex items-center justify-center">
        <ImageOff className="w-4 h-4 text-zinc-400" />
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-16 h-16 rounded-md overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-[#002FA7]"
    >
      <img
        src={url}
        alt=""
        className="w-full h-full object-cover"
        onError={() => setBroken(true)}
      />
    </a>
  );
}

export default function Publish() {
  const [form, setForm] = useState(emptyForm);
  const [imageResults, setImageResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null); // {product_id, message}
  const { canPublish, banner: subBanner, reload: reloadSub } = useSubscriptionGuard();

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const step1SearchImages = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do produto");
      return;
    }
    setSearching(true);
    setImageResults([]);
    try {
      const { data } = await api.post("/search", {
        items: [{ name: form.name, category: form.category }],
      });
      const r = (data && data[0]) || null;
      const imgs = r?.images || [];
      setImageResults(imgs);
      setForm((f) => ({ ...f, images: imgs }));
      if (imgs.length) toast.success(`${imgs.length} imagem(ns) encontradas`);
      else toast.warning("Nenhuma imagem encontrada — você pode gerar os campos mesmo assim");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro na busca");
    } finally {
      setSearching(false);
    }
  };

  const step2GenerateFields = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do produto");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/generate-fields", {
        name: form.name,
        category: form.category,
        image_urls: form.images,
      });
      setForm((f) => ({
        ...f,
        description: data.description || "",
        usage: data.usage || "",
        seo_title: data.seo_title || "",
        seo_description: data.seo_description || "",
        handle: data.handle || "",
        suggested_price: String(data.suggested_price || ""),
        price_reasoning: data.price_reasoning || "",
        sku: data.sku || "",
      }));
      toast.success("Campos gerados pela IA — revise antes de publicar");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro na IA");
    } finally {
      setGenerating(false);
    }
  };

  const step3Publish = async () => {
    if (!form.name.trim() || !form.description.trim()) {
      toast.error("Nome e descrição são obrigatórios");
      return;
    }
    if (!canPublish) {
      toast.error("Publicação bloqueada: assine um plano ou confirme sua assinatura para continuar.");
      return;
    }
    setPublishing(true);
    try {
      const { data } = await api.post("/nuvemshop/publish", {
        name: form.name,
        description: form.description,
        usage: form.usage,
        seo_title: form.seo_title,
        seo_description: form.seo_description,
        handle: form.handle,
        price: parseFloat(form.suggested_price) || 0,
        sku: form.sku,
        stock: parseInt(form.stock) || 0,
        images: form.images,
        published: true,
      });
      if (data.ok) {
        setPublished(data);
        toast.success(data.message);
        reloadSub();
      } else {
        toast.error(data.message || "Falha ao publicar");
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 402) {
        const msg = typeof detail === "object" ? (detail.message || detail.cta) : detail;
        toast.error(msg || "Assinatura necessária para publicar.", { duration: 8000 });
        reloadSub();
      } else {
        toast.error(formatApiErrorDetail(detail) || "Erro ao publicar");
      }
    } finally {
      setPublishing(false);
    }
  };

  const reset = () => {
    setForm(emptyForm);
    setImageResults([]);
    setPublished(null);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 sm:p-10">
        <Toaster position="top-right" richColors />

        <header className="mb-6">
          <div className="eyebrow mb-2">Publicar</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B]">
            Publicar produtos no seu e-commerce
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Informe o produto → buscamos imagens → IA gera descrição, indicação, SEO, preço sugerido e slug → você revisa e publica com 1 clique.
            {" "}
            <Link to="/settings" className="brand-text font-medium underline">
              Configure o Nuvemshop
            </Link>{" "}se ainda não fez.
          </p>
        </header>

        {subBanner}

        <Tabs defaultValue="batch" className="w-full">
          <TabsList className="bg-zinc-100 rounded-md p-1 mb-6">
            <TabsTrigger value="batch"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-sm px-4 py-1.5 text-sm"
              data-testid="publish-tab-batch">
              Lote (até 1000)
            </TabsTrigger>
            <TabsTrigger value="single"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-sm px-4 py-1.5 text-sm"
              data-testid="publish-tab-single">
              Individual
            </TabsTrigger>
          </TabsList>

          <TabsContent value="batch" className="mt-0">
            <BatchPublish />
          </TabsContent>

          <TabsContent value="single" className="mt-0">

        {published ? (
          <div className="border border-emerald-200 bg-emerald-50 rounded-md p-6 text-center" data-testid="publish-success">
            <div className="eyebrow text-emerald-700 mb-2">Publicado</div>
            <h2 className="font-display text-2xl font-semibold text-emerald-900 mb-1">
              {published.message}
            </h2>
            <p className="text-sm text-emerald-700 mb-4">
              Produto criado com sucesso. ID Nuvemshop: <span className="font-mono">{published.product_id}</span>
            </p>
            <Button onClick={reset} className="rounded-md brand-primary text-white" data-testid="publish-another-button">
              Publicar outro produto
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: input + generate */}
            <section className="border border-zinc-200 bg-white rounded-md p-5 space-y-4">
              <div className="eyebrow mb-1">Passo 1 · Produto</div>
              <div>
                <Label className="eyebrow">Nome do produto</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="ex: Dipirona sódica 500mg 20 comprimidos"
                  className="mt-1.5 h-10 rounded-md border-zinc-300"
                  data-testid="publish-name-input"
                />
              </div>
              <div>
                <Label className="eyebrow">Categoria</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                  placeholder="ex: medicamento, cosmético, eletrônico"
                  className="mt-1.5 h-10 rounded-md border-zinc-300"
                  data-testid="publish-category-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <Button
                  onClick={step1SearchImages}
                  disabled={searching || !form.name.trim()}
                  variant="outline"
                  className="rounded-md border-zinc-300"
                  data-testid="publish-search-images-button"
                >
                  {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Buscar imagens
                </Button>
                <Button
                  onClick={step2GenerateFields}
                  disabled={generating || !form.name.trim()}
                  className="rounded-md brand-primary text-white"
                  data-testid="publish-generate-fields-button"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Gerar campos com IA
                </Button>
              </div>

              <div className="pt-2">
                <div className="eyebrow mb-2">Imagens ({imageResults.length})</div>
                <div className="flex gap-2 flex-wrap">
                  {imageResults.length === 0
                    ? [0, 1, 2].map((i) => (
                        <div key={i} className="w-16 h-16 rounded-md bg-zinc-100 border border-zinc-200 flex items-center justify-center">
                          <ImageOff className="w-4 h-4 text-zinc-400" />
                        </div>
                      ))
                    : imageResults.map((u, i) => (
                        <PublishThumb key={`${i}-${u}`} url={u} />
                      ))}
                </div>
              </div>
            </section>

            {/* Right: pre-filled fields */}
            <section className="border border-zinc-200 bg-white rounded-md p-5 space-y-4">
              <div className="eyebrow mb-1">Passo 2 · Revise e Publique</div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="eyebrow">Preço sugerido (R$)</Label>
                  <Input
                    value={form.suggested_price}
                    onChange={(e) => setField("suggested_price", e.target.value)}
                    placeholder="0,00"
                    className="mt-1.5 h-10 rounded-md border-zinc-300 font-mono"
                    data-testid="publish-price-input"
                  />
                  {form.price_reasoning && (
                    <p className="text-[10px] text-zinc-500 mt-1">{form.price_reasoning}</p>
                  )}
                </div>
                <div>
                  <Label className="eyebrow">SKU</Label>
                  <Input
                    value={form.sku}
                    onChange={(e) => setField("sku", e.target.value)}
                    className="mt-1.5 h-10 rounded-md border-zinc-300 font-mono uppercase"
                    data-testid="publish-sku-input"
                  />
                </div>
              </div>

              <div>
                <Label className="eyebrow">URL (handle/slug)</Label>
                <Input
                  value={form.handle}
                  onChange={(e) => setField("handle", e.target.value)}
                  className="mt-1.5 h-10 rounded-md border-zinc-300 font-mono text-sm"
                  data-testid="publish-handle-input"
                />
              </div>

              <div>
                <Label className="eyebrow">Descrição (HTML)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  rows={6}
                  className="mt-1.5 rounded-md border-zinc-300 font-mono text-xs"
                  data-testid="publish-description-input"
                />
              </div>

              <div>
                <Label className="eyebrow">Indicação de uso</Label>
                <Textarea
                  value={form.usage}
                  onChange={(e) => setField("usage", e.target.value)}
                  rows={3}
                  className="mt-1.5 rounded-md border-zinc-300 text-xs"
                  data-testid="publish-usage-input"
                />
              </div>

              <div>
                <Label className="eyebrow">SEO Title (≤ 60 chars)</Label>
                <Input
                  value={form.seo_title}
                  onChange={(e) => setField("seo_title", e.target.value)}
                  maxLength={60}
                  className="mt-1.5 h-10 rounded-md border-zinc-300"
                  data-testid="publish-seo-title-input"
                />
              </div>

              <div>
                <Label className="eyebrow">SEO Description (≤ 160 chars)</Label>
                <Textarea
                  value={form.seo_description}
                  onChange={(e) => setField("seo_description", e.target.value)}
                  rows={2}
                  maxLength={160}
                  className="mt-1.5 rounded-md border-zinc-300 text-xs"
                  data-testid="publish-seo-desc-input"
                />
              </div>

              <div>
                <Label className="eyebrow">Estoque inicial</Label>
                <Input
                  type="number"
                  value={form.stock}
                  onChange={(e) => setField("stock", e.target.value)}
                  className="mt-1.5 h-10 rounded-md border-zinc-300 w-32"
                  data-testid="publish-stock-input"
                />
              </div>

              <Button
                onClick={step3Publish}
                disabled={publishing || !form.description || !canPublish}
                className="w-full h-12 rounded-md brand-primary text-white font-medium disabled:opacity-50"
                data-testid="publish-submit-button"
              >
                {publishing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publicando no Nuvemshop...</>
                ) : !canPublish ? (
                  <>Assinatura necessária — ver planos</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Publicar no Nuvemshop</>
                )}
              </Button>
            </section>
          </div>
        )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
